'use client';

import React from 'react';
import Link from 'next/link';
import dynamic from 'next/dynamic';
import {
    ClockIcon, ChevronRightIcon, CakePricingIcon, RecipeScalerIcon, PercentIcon, MeasurementIcon,
    BakingPanIcon, ManagementIcon,
    IngredientSwapIcon,
    DollarSignIcon, ThermometerIcon, YeastIcon,
    ChecklistIcon, HeartIcon,
    WaterDropIcon,
    PastryBagIcon,
    PaletteIcon,
    FlavorWheelIcon,
    MacaronIcon,
    ClipboardIcon,
    BoxIcon,
    CalendarIcon,
    StarIcon,
    ShieldCheckIcon,
    ArrowRightIcon,
    RecipeCostIcon
} from './Icons';
import { ViewAllButton } from './ViewAllButton';
import type { ToolId } from './utils/slugs';
import { toolIdToSlug } from './utils/slugs';
import { LemonSqueezyButton } from './LemonSqueezyButton';
import { blogPosts as allBlogPosts } from '../data/blogPosts';
import AdSenseAd from './AdSenseAd';
import OptimizedImage from './OptimizedImage';
import styles from './Common.module.css';
import { BundleCarousel } from './BundleCarousel';

const HeroIllustration = dynamic(() => import('./HeroIllustration'));
const InternalLinkRecommendations = dynamic(() => import('./InternalLinkRecommendations').then(mod => ({ default: mod.InternalLinkRecommendations })));
const PremiumPromo = dynamic(() => import('./PremiumPromo').then(mod => ({ default: mod.PremiumPromo })));

const FeatureCard = ({ icon, title, children }: any) => (
    <div className="bg-white p-8 rounded-2xl shadow-sm border border-brand-border text-center hover:shadow-xl hover:-translate-y-2 transition-all duration-300 group h-full flex flex-col">
        <div className="inline-block bg-brand-pink-light text-brand-pink p-4 rounded-2xl mx-auto mb-6 group-hover:bg-brand-pink group-hover:text-white transition-colors transform group-hover:rotate-3">
            {icon}
        </div>
        <h3 className="text-xl font-serif font-bold text-brand-text-title mb-3">{title}</h3>
        <p className="text-brand-text-body/90 text-sm leading-relaxed flex-grow">{children}</p>
    </div>
);

const Tier1ToolCard = ({ icon, title, children, toolId, saasText }: any) => (
    <div className="bg-white p-6 rounded-2xl border border-brand-border shadow-sm hover:shadow-xl transition-all flex flex-col group h-full">
        <div className="bg-brand-pink/10 text-brand-pink mb-4 rounded-xl p-3 w-max group-hover:scale-110 transition-transform">{icon}</div>
        <h3 className="text-lg font-serif font-bold text-brand-text-title mb-2">{title}</h3>
        <p className="text-sm text-brand-text-body/80 mb-6 flex-grow">{children}</p>
        <div className="flex flex-col gap-2">
            <Link href={toolIdToSlug(toolId)} className="w-full text-center bg-brand-pink text-white py-3 rounded-xl text-sm font-bold shadow-md hover:bg-brand-pink/90 transition-colors min-h-[44px] flex items-center justify-center">
                Try Free Calculator
            </Link>
            <Link href="/saas" className="w-full text-center text-brand-pink text-xs font-bold hover:underline py-2">
                → {saasText || "Automate with Bakery Pro"}
            </Link>
        </div>
    </div>
);

const Tier2ToolCard = ({ icon, title, children, toolId }: any) => (
    <Link href={toolIdToSlug(toolId)} className="bg-stone-50/50 p-5 rounded-2xl border border-transparent hover:border-brand-pink/20 hover:bg-white hover:shadow-lg transition-all group flex flex-col h-full">
        <div className="text-brand-pink mb-3 group-hover:scale-110 transition-transform">{icon}</div>
        <h3 className="text-base font-serif font-bold text-brand-text-title mb-1">{title}</h3>
        <p className="text-xs text-brand-text-body/70 flex-grow">{children}</p>
        <div className="mt-3 flex items-center text-xs font-bold text-brand-pink opacity-0 group-hover:opacity-100 transition-opacity">
            Try Free <ChevronRightIcon className="w-3 h-3 ml-1" />
        </div>
    </Link>
);

const TrustBadge = ({ icon, children }: any) => (
    <div className="flex items-center gap-2 text-stone-500 font-bold text-xs uppercase tracking-widest whitespace-nowrap">
        <span className="text-brand-pink">{icon}</span>
        {children}
    </div>
);

const TestimonialCard = ({ quote, author }: any) => (
    <div className="bg-white p-8 rounded-2xl border border-brand-border shadow-sm italic text-brand-text-body/90 relative">
        <div className="absolute top-0 left-6 -translate-y-1/2 bg-brand-pink text-white px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider not-italic">verified user</div>
        "{quote}"
        <div className="mt-4 not-italic font-bold text-brand-text-title text-sm">— {author}</div>
    </div>
);

const FaqItem = ({ title, children }: any) => (
    <details className="border border-brand-border rounded-xl bg-white overflow-hidden hover:border-brand-pink/50 group">
        <summary className={`${styles.summaryReset} p-5 flex justify-between items-center font-bold text-brand-text-title group-hover:text-brand-pink cursor-pointer`}>
            {title}
            <ChevronRightIcon className={`${styles.accordionArrow} w-5 h-5 text-brand-pink transition-transform`} />
        </summary>
        <div className="px-5 pb-5 text-brand-text-body/90 border-t border-brand-border pt-4">
            {children}
        </div>
    </details>
);

export const HomepageContent = ({ setActiveTool, handleNavigate }: any) => {
    return (
        <div className="p-3 sm:p-4 md:p-6 lg:p-10 fade-in">
            {/* SEO Schema Markup */}
            <script
                type="application/ld+json"
                dangerouslySetInnerHTML={{
                    __html: JSON.stringify({
                        "@context": "https://schema.org",
                        "@type": "SoftwareApplication",
                        "name": "Bakery Pro",
                        "operatingSystem": "Web",
                        "applicationCategory": "BusinessApplication",
                        "offers": {
                            "@type": "Offer",
                            "price": "17.00",
                            "priceCurrency": "USD"
                        },
                        "aggregateRating": {
                            "@type": "AggregateRating",
                            "ratingValue": "4.9",
                            "reviewCount": "87"
                        }
                    })
                }}
            />

            {/* HERO SECTION */}
            <header className="relative my-6 sm:my-8 md:my-12 lg:my-16 overflow-hidden rounded-3xl bg-gradient-to-br from-brand-pink-light/30 to-white border border-brand-border/50 shadow-sm">
                <div className="absolute top-0 right-0 -mt-20 -mr-20 w-96 h-96 bg-brand-pink/10 rounded-full blur-3xl"></div>
                <div className="relative z-10 max-w-7xl mx-auto px-6 py-12 md:py-24 flex flex-col lg:flex-row items-center gap-12">
                    <div className="flex-1 text-center lg:text-left">
                        <div className="inline-block px-4 py-1.5 rounded-full bg-brand-pink/10 text-brand-pink font-bold text-xs md:text-sm mb-6 tracking-wide uppercase">
                            The #1 Free Toolkit for Home Bakers & Cottage Food Businesses
                        </div>
                        <h1 className="font-serif text-4xl md:text-6xl font-bold text-brand-text-title leading-[1.1] mb-6">
                            The #1 Free Toolkit <br />
                            <span className="text-transparent bg-clip-text bg-gradient-to-r from-brand-pink to-brand-pink-dark">for Home Bakers</span>
                        </h1>
                        <p className="text-lg md:text-xl text-brand-text-body mb-8 leading-relaxed max-w-2xl mx-auto lg:mx-0">
                            Stop losing money on underpriced cakes. Get professional pricing, scaling & inventory tools—free.
                        </p>
                        <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                            <Link href="/tools/all-tools" className="bg-brand-pink text-white font-bold py-4 px-10 rounded-full shadow-lg hover:shadow-xl hover:-translate-y-1 transition-all flex items-center justify-center min-h-[44px] w-full sm:w-auto">
                                Explore Free Tools
                            </Link>
                            <LemonSqueezyButton 
                                checkoutUrl="https://sweetysbakery.lemonsqueezy.com/checkout/buy/5ded6330-c328-4150-9bd0-e978e8042454?embed=1"
                                buttonText="Upgrade to Bakery Pro"
                                seoText="Buy Bakery Profit Calculator BUNDLE | Google Sheets + Offline App | Cake Costing Template | Recipe Pricing Software | Home Bakery Business"
                                className="bg-white text-brand-pink font-bold py-4 px-10 rounded-full shadow-sm border-2 border-brand-pink/20 hover:bg-brand-pink/5 hover:-translate-y-1 transition-all flex items-center justify-center min-h-[44px] w-full sm:w-auto gap-2 group"
                            />
                        </div>
                        
                        {/* TRUST BADGES */}
                        <div className="mt-12 flex flex-wrap justify-center lg:justify-start gap-6 opacity-80">
                            <TrustBadge icon={<StarIcon className="w-4 h-4" />}>Used by 1,200+ home bakers</TrustBadge>
                            <TrustBadge icon={<HeartIcon className="w-4 h-4" />}>4.9/5 from 87 reviews</TrustBadge>
                            <TrustBadge icon={<ShieldCheckIcon className="w-4 h-4" />}>30-day money-back guarantee</TrustBadge>
                        </div>
                    </div>
                    <div className="w-full lg:flex-1 justify-center relative mt-12 lg:mt-0 mx-auto max-w-lg lg:max-w-none">
                        <div className="relative z-10 w-full aspect-video bg-white rounded-2xl shadow-2xl border-4 border-white overflow-hidden transform lg:rotate-2 hover:rotate-0 transition-transform duration-500">
                            <iframe 
                                className="absolute top-0 left-0 w-full h-full"
                                src="https://www.youtube.com/embed/9-wiRFrp2Tc" 
                                title="Bakery Pro Demo Video" 
                                frameBorder="0" 
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" 
                                allowFullScreen
                            ></iframe>
                        </div>
                        <div className="hidden lg:block absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[120%] h-[120%] border border-brand-pink/10 rounded-full"></div>
                    </div>
                </div>
            </header>

            {/* TIER 1 TOOLS GRID */}
            <section id="popular-tools" className="my-20">
                <div className="text-center mb-12">
                    <h2 className="text-4xl font-serif font-bold text-brand-text-title mb-4">Our Most Popular Free Tools</h2>
                    <p className="text-brand-text-body/70 max-w-2xl mx-auto px-4">Elite calculators to help you spend more time baking and less time on math.</p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-7xl mx-auto px-4">
                    <Tier1ToolCard 
                        toolId="cake-pricing" 
                        icon={<CakePricingIcon className="w-8 h-8" />} 
                        title="Cake Pricing"
                        saasText="Automate pricing + orders with Bakery Pro"
                    >
                        Price your cakes accurately by calculating COGS and labor.
                    </Tier1ToolCard>
                    <Tier1ToolCard 
                        toolId="recipe-scaler" 
                        icon={<RecipeScalerIcon className="w-8 h-8" />} 
                        title="Recipe Scaler"
                        saasText="Scale entire menus with Bakery Pro"
                    >
                        Scale your yields perfectly while maintaining consistency.
                    </Tier1ToolCard>
                    <Tier1ToolCard 
                        toolId="cost-per-recipe" 
                        icon={<DollarSignIcon className="w-8 h-8" />} 
                        title="Cost Calculator"
                        saasText="Real-time costing with Bakery Pro"
                    >
                        Determine the exact production cost of any recipe batch.
                    </Tier1ToolCard>
                    <Tier1ToolCard 
                        toolId="bakery-order-planner" 
                        icon={<CalendarIcon className="w-8 h-8" />} 
                        title="Order Planner"
                        saasText="Professional order CRM with Bakery Pro"
                    >
                        Organize your baking tasks and create efficient schedules.
                    </Tier1ToolCard>
                </div>
                <div className="mt-12 text-center px-4">
                    <Link href="/tools/all-tools" className="inline-flex items-center justify-center gap-2 px-8 py-3 bg-stone-100 text-stone-700 font-bold rounded-full hover:bg-stone-200 transition-all w-full sm:w-auto min-h-[44px]">
                        View All Tools <ChevronRightIcon className="w-5 h-5" />
                    </Link>
                </div>
            </section>

            {/* SAAS OFFER SECTION */}
            <section className="my-24 py-20 bg-stone-900 rounded-[2rem] sm:rounded-[3rem] text-white overflow-hidden relative">
                <div className="absolute top-0 right-0 w-96 h-96 bg-brand-pink/20 rounded-full blur-[100px] -translate-y-1/2 translate-x-1/2"></div>
                <div className="max-w-5xl mx-auto px-8 relative z-10 text-center lg:text-left flex flex-col lg:flex-row items-center gap-16">
                    <div className="flex-1">
                        <div className="inline-block px-4 py-1 rounded-full bg-brand-pink text-white text-[10px] font-black uppercase tracking-[0.2em] mb-6">
                            🎁 Launch Sale: Save 60% (Ends JUIN30)
                        </div>
                        <h2 className="text-4xl md:text-5xl font-serif font-bold mb-6 leading-tight">Stop Guessing. <br />Start Profiting.</h2>
                        <ul className="space-y-4 mb-10 text-stone-400">
                            <li className="flex items-center gap-3"><ChecklistIcon className="w-5 h-5 text-brand-pink" /> <Link href="/tools/cake-pricing" className="hover:text-white underline decoration-brand-pink/30 transition-colors">Advanced Pricing Engine</Link> (like our free calculator, but automated)</li>
                            <li className="flex items-center gap-3"><ChecklistIcon className="w-5 h-5 text-brand-pink" /> <Link href="/tools/order-management" className="hover:text-white underline decoration-brand-pink/30 transition-colors">Order Dashboard</Link> (track all clients in one place)</li>
                            <li className="flex items-center gap-3"><ChecklistIcon className="w-5 h-5 text-brand-pink" /> <Link href="/tools/inventory-management" className="hover:text-white underline decoration-brand-pink/30 transition-colors">Smart Inventory Alerts</Link> (never run out of flour)</li>
                        </ul>
                        <div className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-6">
                            <div className="flex flex-col">
                                <span className="text-4xl font-bold">$17 <span className="text-lg text-stone-500 line-through font-normal">$47</span></span>
                                <span className="text-[10px] text-brand-pink uppercase font-black tracking-widest mt-1 italic animate-pulse">Save 60% Today Only</span>
                            </div>
                            <LemonSqueezyButton 
                                checkoutUrl="https://sweetysbakery.lemonsqueezy.com/checkout/buy/5ded6330-c328-4150-9bd0-e978e8042454?embed=1"
                                buttonText="Get Instant Access"
                                seoText="Buy Bakery Profit Calculator BUNDLE | Google Sheets + Offline App | Cake Costing Template | Recipe Pricing Software | Home Bakery Business"
                                className="bg-brand-pink hover:bg-brand-pink-dark text-white px-10 py-4 rounded-full font-black uppercase tracking-widest text-xs shadow-xl transition-all flex items-center justify-center gap-2 w-full sm:w-auto min-h-[56px] group"
                            />
                        </div>
                        <p className="mt-6 text-brand-pink font-bold text-sm flex items-center justify-center lg:justify-start gap-2">
                             💖 Support us by buying this bundle!
                        </p>
                        <p className="mt-2 text-stone-500 text-[10px] font-bold flex items-center justify-center lg:justify-start gap-1">
                             ✅ 30-Day Money-Back Guarantee
                        </p>
                    </div>
                </div>
            </section>

            {/* SAAS BUNDLE IMAGES SECTION */}
            <section className="my-24 max-w-5xl mx-auto px-4">
                <div className="text-center mb-16">
                    <h2 className="text-4xl font-serif font-bold text-brand-text-title mb-4">What's Inside The Bundle?</h2>
                    <p className="text-brand-text-body/70 text-lg max-w-2xl mx-auto">Get everything you need to price your cakes perfectly and manage your bakery business.</p>
                </div>
                <BundleCarousel />
            </section>

            {/* TIER 2 TOOLS GRID */}
            <section className="my-24">
                <div className="text-center mb-12">
                    <h2 className="text-3xl font-serif font-bold text-brand-text-title mb-4">Essential Baking Utilities</h2>
                    <p className="text-brand-text-body/70 px-4">The heavy-duty tools you need for everyday precision in the kitchen.</p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 max-w-5xl mx-auto px-4">
                    <Tier2ToolCard toolId="measurement-converter" icon={<MeasurementIcon className="w-6 h-6" />} title="Measurement">Convert anything from cups to grams instantly.</Tier2ToolCard>
                    <Tier2ToolCard toolId="bakers-percentage" icon={<PercentIcon className="w-6 h-6" />} title="Baker's %">Master your formulas for perfect consistency.</Tier2ToolCard>
                    <Tier2ToolCard toolId="dough-hydration" icon={<WaterDropIcon className="w-6 h-6" />} title="Dough Hydration">Precision tools for artisanal bread bakers.</Tier2ToolCard>
                    <Tier2ToolCard toolId="temperature-converter" icon={<ThermometerIcon className="w-6 h-6" />} title="Temperature">Oven and temp adjustments for any climate.</Tier2ToolCard>
                    <Tier2ToolCard toolId="ganache-calculator" icon={<WaterDropIcon className="w-6 h-6" />} title="Ganache Ratio">Perfect chocolate ratios for every application.</Tier2ToolCard>
                    <Tier2ToolCard toolId="buttercream-calculator" icon={<PastryBagIcon className="w-6 h-6" />} title="Buttercream">Calculate exactly how much frosting you need.</Tier2ToolCard>
                </div>
            </section>

            {/* RECIPES SECTION (Below fold) */}
            <section className="my-24 bg-stone-50 -mx-3 sm:-mx-4 md:-mx-6 lg:-mx-10 px-3 sm:px-4 md:px-6 lg:px-10 py-20 border-y border-stone-100">
                <div className="flex flex-col md:flex-row items-center justify-between mb-12 gap-6 max-w-7xl mx-auto">
                    <div className="text-center md:text-left">
                        <h2 className="text-3xl md:text-4xl font-serif font-bold text-brand-text-title mb-2">Love Baking? Try Our Free Recipes</h2>
                        <p className="text-brand-text-body/70">Expert-tested formulas for cakes, cookies, and breads.</p>
                    </div>
                    <ViewAllButton href={toolIdToSlug('baking-blog')} label="Explore All Recipes" />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
                    {allBlogPosts.filter(p => p.type === 'recipe').slice(0, 3).map((post, i) => (
                        <Link key={post.id} href={`/recipes/${post.slug}`} className="group flex flex-col h-full bg-white rounded-2xl overflow-hidden shadow-sm border border-brand-border hover:shadow-xl transition-all">
                            <div className="relative aspect-video overflow-hidden">
                                <OptimizedImage src={post.imageUrl} alt={post.title} fill containerClassName="h-full w-full" className="transition-transform group-hover:scale-110" />
                            </div>
                            <div className="p-6 flex flex-col flex-grow">
                                <h3 className="text-xl font-serif font-bold text-brand-text-title mb-3 group-hover:text-brand-pink transition-colors">{post.title}</h3>
                                <p className="text-brand-text-body text-sm line-clamp-2 mb-6 flex-grow">{post.excerpt}</p>
                                <div className="flex items-center text-brand-pink font-bold text-sm">Read Recipe <ChevronRightIcon className="w-4 h-4 ml-1" /></div>
                            </div>
                        </Link>
                    ))}
                </div>
            </section>

            {/* TESTIMONIALS */}
            <section className="my-24 max-w-5xl mx-auto px-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <TestimonialCard 
                        quote="This calculator saved me 3 hours on my first custom order! I was always guessing my prices before." 
                        author="Maria L. (Cottage Food Baker)" 
                    />
                    <TestimonialCard 
                        quote="Finally priced my cookies profitably. Made $300 extra this month just by knowing my actual COGS!" 
                        author="James K. (Home Bakery Owner)" 
                    />
                </div>
            </section>

            {/* FAQs (SaaS Focused) */}
            <section className="my-24 px-4">
                <h2 className="text-4xl font-serif font-bold text-brand-text-title text-center mb-12">Common Questions</h2>
                <div className="max-w-3xl mx-auto space-y-4">
                    <FaqItem title="How is Bakery Pro different from the free tools?">
                        While our free tools are great for one-off calculations, <strong>Bakery Pro</strong> is a complete business operating system. It includes client management, professional invoicing, recipe cloud storage, and historical cost tracking.
                    </FaqItem>
                    <FaqItem title="Can I export my data from the free calculators?">
                        The free calculators provide instant results for your session. To export PDF invoices or save your costing sheets permanently, you'll need a <strong>Bakery Pro</strong> account.
                    </FaqItem>
                    <FaqItem title="What if I'm not satisfied with my purchase?">
                        We offer a 30-day money-back guarantee. If Bakery Pro doesn't transform your bakery business, we'll refund your payment in full—no questions asked.
                    </FaqItem>
                </div>
            </section>

            {/* FINAL CTA SECTION */}
            <section className="my-24 text-center py-20 bg-brand-pink-light/30 rounded-[2rem] sm:rounded-[3rem] border border-brand-pink/10 mx-4 px-4">
                <h2 className="text-4xl font-serif font-bold text-brand-text-title mb-6">Ready to bake smarter?</h2>
                <p className="text-brand-text-body/70 mb-10 max-w-xl mx-auto text-lg">Join 1,200+ bakers who are using our tools to build profitable businesses.</p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                    <Link href="/tools/all-tools" className="bg-white text-stone-700 font-bold py-4 px-10 rounded-full shadow-sm border border-stone-200 hover:bg-stone-50 transition-all w-full sm:w-auto min-h-[44px] flex items-center justify-center">
                        Browse All Free Tools
                    </Link>
                    <LemonSqueezyButton 
                        checkoutUrl="https://sweetysbakery.lemonsqueezy.com/checkout/buy/5ded6330-c328-4150-9bd0-e978e8042454?embed=1"
                        buttonText="Get Bakery Pro"
                        price="17"
                        seoText="Buy Bakery Profit Calculator BUNDLE | Google Sheets + Offline App | Cake Costing Template | Recipe Pricing Software | Home Bakery Business"
                        className="bg-brand-pink text-white font-bold py-4 px-10 rounded-full shadow-lg hover:bg-brand-pink-dark transition-all w-full sm:w-auto min-h-[44px] flex items-center justify-center gap-2 group"
                    />
                </div>
            </section>
        </div>
    );
};
